

















































































































import Vue from "vue";
import { parseAppointmentTime } from "@/utils/appointmentTimezone/appointmentTimezone";
import WaitingRoom from "@/components/Session/WaitingRoom.vue";
import OtpChallenge from "@/components/GuestJoin/OtpChallenge.vue";
import GuestVideoRoom from "@/components/GuestJoin/GuestVideoRoom.vue";
import {
  startGuestJoin,
  sendGuestJoinCode,
  GuestJoinStartResponse,
} from "@/utils/guestJoin/guestJoinApi";
import { getSupportEmail } from "@/utils/supportEmail/supportEmail";
import { returnHome } from "@/utils/embed/partnerEmbed";
import { setGuestSession } from "@/utils/sentryScrub/sentryScrub";
import { trackEvent } from "@/utils/analytics/analytics";
import {
  GUEST_JOIN_PAGE_VIEWED,
  GUEST_JOIN_SESSION_ENDED,
} from "@/utils/analyticsEvents";

// State machine per SPA-2/SPA-3/SPA-4: loading -> too_early|otpRequired|unavailable|error,
// otpRequired -> otp (on requestCode) -> connecting (on verify) -> ended.
// "verifying" and "in_room" from the ticket aren't page-level states:
// verifying lives inside OtpChallenge's own submit-button state (so the
// entered code stays on screen while the request is in flight), and once
// mounted VideoRoom owns the connecting/in-room distinction through its own
// status display.
type GuestJoinState =
  | "loading"
  | "unavailable"
  | "too_early"
  | "otpRequired"
  | "otp"
  | "connecting"
  | "ended"
  | "error";

// A too_early response never resolves itself — nothing pushes the page onward
// once the window opens — so it has to be re-checked for the guest to advance
// without reloading. The delay comes from appointment_start itself,
// floored and capped: floored because the backend decides the join window on
// its own rule and can still answer too_early after the start time has passed
// (unfloored that would be a request per second), capped so a distant start
// re-checks periodically rather than resting on one long timer that a sleeping
// device would never fire.
const START_RECHECK_MIN_DELAY_MS = 40000;
const START_RECHECK_MAX_DELAY_MS = 600000;

// Mirrors OtpChallenge's own resend cooldown — guards against a guest hammering
// "Send me a code" while a request is in flight or just after one settles.
const REQUEST_CODE_COOLDOWN_SECONDS = 30;

// Set by the token-stripping snippet in public/index.html.
type GuestJoinWindow = Window & { __guestJoinToken?: string };

export default Vue.extend({
  name: "GuestJoin",
  components: { WaitingRoom, OtpChallenge, GuestVideoRoom },
  data() {
    return {
      token: "",
      state: "loading" as GuestJoinState,
      challengeToken: "",
      phoneLast4: "",
      appointmentStart: null as string | null,
      clinicianFirstName: "",
      guestSessionToken: "",
      expiredNotice: false,
      appointmentStartPassed: false,
      startInFlight: false,
      startRecheckTimeoutId: null as number | null,
      sendCodeInFlight: false,
      requestCodeError: "",
      requestCodeSecondsLeft: 0,
      requestCodeIntervalId: null as ReturnType<typeof setInterval> | null,
    };
  },
  computed: {
    clientLogo(): string {
      const hardcoded = this.$store.getters.hardcoded;
      if (hardcoded && hardcoded.logo) {
        return hardcoded.logo;
      }
      return this.$store.getters.client.client_logo;
    },
    supportEmail(): string {
      return getSupportEmail();
    },
    waitingBooking(): Record<string, unknown> {
      // appointment_start has no offset marker ("2026-09-14 14:20:00") — it's
      // the same "YYYY-MM-DD HH:mm:ss" business-time format the rest of the
      // app already parses via parseAppointmentTime (APPOINTMENT_TIME_ZONE,
      // Europe/Dublin unless overridden), not UTC. Treating it as UTC skewed
      // the countdown by the Dublin/UTC offset.
      const parsed = parseAppointmentTime(this.appointmentStart);
      const startMs = parsed ? parsed.valueOf() : null;
      return {
        startDublinMs: startMs,
        startTime: this.appointmentStart,
        // The backend never returns an end time for this flow.
        endTime: this.appointmentStart,
        clinician: { name: this.clinicianFirstName || "your clinician" },
      };
    },
  },
  created(): void {
    // index.html lifts the token out of the URL before any third-party tag
    // loads, so it normally arrives in memory; the route param is the fallback
    // for a page served without that snippet.
    this.token = String(
      (window as GuestJoinWindow).__guestJoinToken ||
        this.$route.params.token ||
        "",
    );
    setGuestSession(true);
    this.hardenJoinUrl();
    trackEvent(GUEST_JOIN_PAGE_VIEWED);
    this.loadStart();
  },
  beforeDestroy(): void {
    this.clearStartRecheck();
    this.clearRequestCodeCooldown();
    setGuestSession(false);
  },
  methods: {
    // Same technique as NotLoggedIn.vue's hardenResetPage(): suppress the
    // Referer header and drop the token from browser history immediately.
    hardenJoinUrl(): void {
      let referrerMeta = document.querySelector('meta[name="referrer"]');
      if (!referrerMeta) {
        referrerMeta = document.createElement("meta");
        referrerMeta.setAttribute("name", "referrer");
        document.head.appendChild(referrerMeta);
      }
      referrerMeta.setAttribute("content", "no-referrer");
      if (window.history && window.history.replaceState) {
        window.history.replaceState(
          window.history.state,
          document.title,
          "/join",
        );
      }
    },
    clearStartRecheck(): void {
      if (this.startRecheckTimeoutId !== null) {
        clearTimeout(this.startRecheckTimeoutId);
        this.startRecheckTimeoutId = null;
      }
    },
    msUntilAppointmentStart(): number {
      const parsed = parseAppointmentTime(this.appointmentStart);
      // An unparseable/absent start reads as "already due" — the floored
      // re-check then decides when we look again.
      return parsed ? parsed.valueOf() - Date.now() : 0;
    },
    scheduleStartRecheck(): void {
      const delay = Math.min(
        Math.max(this.msUntilAppointmentStart(), START_RECHECK_MIN_DELAY_MS),
        START_RECHECK_MAX_DELAY_MS,
      );
      this.startRecheckTimeoutId = window.setTimeout(() => {
        this.startRecheckTimeoutId = null;
        this.loadStart();
      }, delay);
    },
    async loadStart(): Promise<void> {
      // /start only checks the join window's status — never let a second call
      // overlap, whatever set it off (created(), a scheduled re-check, retry(),
      // or an expired-session re-request all funnel through here).
      if (this.startInFlight) return;
      this.startInFlight = true;
      this.clearStartRecheck();
      this.state = "loading";
      try {
        const response: GuestJoinStartResponse = await startGuestJoin(
          this.token,
        );
        if (!response.success) {
          this.state = "unavailable";
          return;
        }
        if (response.state === "too_early") {
          this.appointmentStart = response.appointment_start ?? null;
          this.clinicianFirstName = response.clinician_first_name ?? "";
          this.appointmentStartPassed = this.msUntilAppointmentStart() <= 0;
          this.state = "too_early";
          this.scheduleStartRecheck();
          return;
        }
        // Only remaining case per the confirmed contract is state: "otp_required".
        // /start only checks the join window now — it doesn't send the SMS, so
        // there's no challenge_token yet; requestCode() gets one from send-code.
        this.appointmentStart = response.appointment_start ?? null;
        this.clinicianFirstName = response.clinician_first_name ?? "";
        this.phoneLast4 = response.phone_last4 ?? "";
        this.state = "otpRequired";
      } catch {
        this.state = "error";
      } finally {
        this.startInFlight = false;
      }
    },
    clearRequestCodeCooldown(): void {
      if (this.requestCodeIntervalId !== null) {
        clearInterval(this.requestCodeIntervalId);
        this.requestCodeIntervalId = null;
      }
    },
    startRequestCodeCooldown(): void {
      this.requestCodeSecondsLeft = REQUEST_CODE_COOLDOWN_SECONDS;
      this.clearRequestCodeCooldown();
      this.requestCodeIntervalId = setInterval(() => {
        this.requestCodeSecondsLeft = Math.max(
          0,
          this.requestCodeSecondsLeft - 1,
        );
        if (this.requestCodeSecondsLeft === 0) {
          this.clearRequestCodeCooldown();
        }
      }, 1000);
    },
    async requestCode(): Promise<void> {
      if (this.sendCodeInFlight || this.requestCodeSecondsLeft > 0) return;
      this.sendCodeInFlight = true;
      this.requestCodeError = "";
      try {
        const response = await sendGuestJoinCode(this.token);
        if (response.success && response.challenge_token) {
          this.challengeToken = response.challenge_token;
          this.phoneLast4 = response.phone_last4 ?? this.phoneLast4;
          this.appointmentStart =
            response.appointment_start ?? this.appointmentStart;
          this.clinicianFirstName =
            response.clinician_first_name ?? this.clinicianFirstName;
          this.state = "otp";
          return;
        }
        this.requestCodeError =
          response.err ||
          "We couldn't send the code just now. Please try again.";
      } catch {
        this.requestCodeError =
          "We couldn't send the code just now. Please try again.";
      } finally {
        this.sendCodeInFlight = false;
        this.startRequestCodeCooldown();
      }
    },
    handleOtpVerified({
      guestSessionToken,
    }: {
      guestSessionToken: string;
    }): void {
      this.guestSessionToken = guestSessionToken;
      this.expiredNotice = false;
      this.state = "connecting";
    },
    handleOtpLocked(): void {
      this.state = "unavailable";
    },
    handleSessionExpired(): void {
      // Never drag the guest back off a terminal screen — re-running /start on
      // an already-consumed invite renders "this link isn't available".
      if (this.state === "ended") return;
      this.expiredNotice = true;
      this.loadStart();
    },
    handleEnded(): void {
      trackEvent(GUEST_JOIN_SESSION_ENDED);
      this.state = "ended";
    },
    retry(): void {
      this.loadStart();
    },
    // Same helper WaitingRoom uses to leave: hands the event to the host when
    // embedded, otherwise routes to "/". A guest has no session, so LoggedIn's
    // own guard forwards them from "/" to the sign-in screen.
    goHome(): void {
      returnHome(this.$router, {
        type: "sl:close",
        v: 1,
        reason: "guest_session_ended",
      });
    },
  },
});
